<template>
  <v-card class="my-2">
    <!-- Video Export Overlay -->
    <v-overlay :value="isRecording" z-index="999" opacity="0.8">
      <div class="d-flex flex-column align-center justify-center">
        <v-progress-circular 
            :value="recordingProgress" 
            size="80" 
            width="8" 
            color="white" 
            class="mb-4"
        >
            {{ recordingProgress }}%
        </v-progress-circular>
        <div class="text-h6 white--text">Generating Video...</div>
        <div class="text-caption white--text mt-2">Please do not switch tabs</div>
      </div>
    </v-overlay>

    <v-card-title>
      {{ selectedItem.name }}
      <v-spacer></v-spacer>
      <!-- Help Button -->
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
            <v-btn icon color="info" @click="showHelpDialog = true" v-bind="attrs" v-on="on">
                <v-icon>{{icons.mdiHelpCircleOutline}}</v-icon>
            </v-btn>
        </template>
        <span>Help & Shortcuts</span>
      </v-tooltip>
    </v-card-title>
    
    <v-card-text>
      <!-- Toolbar Controls -->
      <div class="d-flex flex-wrap align-center mb-4 gap-2">
         <!-- Toggles -->
        <v-btn-toggle multiple dense class="mr-4">
            <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" @click="showAOIs = !showAOIs" icon :color="showAOIs ? 'primary' : ''" :loading="aoiLoading"><v-icon>{{icons.mdiDrawingBox}}</v-icon></v-btn>
                </template>
                <span>Toggle AOIs</span>
            </v-tooltip>
             <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" @click="showHeatmap = !showHeatmap" icon :color="showHeatmap ? 'primary' : ''" :loading="heatmapLoading"><v-icon>{{icons.mdiFire}}</v-icon></v-btn>
                </template>
                <span>Toggle Heatmap</span>
            </v-tooltip>
             <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" @click="showScanpath = !showScanpath" icon :color="showScanpath ? 'primary' : ''" :loading="scanpathLoading"><v-icon>{{icons.mdiMapMarkerPath}}</v-icon></v-btn>
                </template>
                <span>Toggle Scanpath</span>
            </v-tooltip>
             <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" @click="isGrayscale = !isGrayscale" icon :color="isGrayscale ? 'grey darken-3' : ''"><v-icon>{{icons.mdiInvertColors}}</v-icon></v-btn>
                </template>
                <span>Toggle B&W</span>
            </v-tooltip>
             <v-tooltip bottom v-if="zones.length">
                <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" @click="showKPIs = !showKPIs" :color="showKPIs ? 'secondary' : ''" :loading="analysisLoading"><v-icon>{{icons.mdiChartBar}}</v-icon></v-btn>
                </template>
                <span>KPI Overlay</span>
            </v-tooltip>
            <v-tooltip bottom v-if="zones.length">
                <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" @click="showKPITable=true" :loading="analysisLoading"><v-icon>{{icons.mdiTableLarge}}</v-icon></v-btn>
                </template>
                <span>View Data Table</span>
            </v-tooltip>
        </v-btn-toggle>
        

        <!-- KPI Metric Selector -->
        <v-select 
            v-if="showKPIs" 
            v-model="selectedKpiMetric" 
            :items="kpiMetricsOptions" 
            label="Metrics" 
            multiple 
            small-chips
            deletable-chips
            dense 
            outlined 
            hide-details 
            class="mr-4" 
            style="max-width: 300px;">
        </v-select>

        <!-- Drawing Modes -->
        <v-btn-toggle v-model="currentMode" outlined class="mr-4">
             <v-tooltip v-for="option in modeOptions" :key="option.value" bottom>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" :value="option.value" icon @click="currentMode = option.value"><v-icon>{{ option.icon }}</v-icon></v-btn>
                </template>
                <span>{{ option.label }}</span>
            </v-tooltip>
        </v-btn-toggle>

        <!-- AOI Edit Actions -->
        <template v-if="selectedZoneIndices.length > 0">
            <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn icon @click="copySelectedAOIs" v-bind="attrs" v-on="on"><v-icon>{{icons.mdiContentCopy}}</v-icon></v-btn>
                </template>
                <span>Copy Selected (Ctrl+C)</span>
            </v-tooltip>
            <v-divider vertical class="mx-2 my-2"></v-divider>
        </template>
        
        <v-tooltip bottom v-if="clipboardAOIs.length > 0">
            <template v-slot:activator="{ on, attrs }">
                <v-btn icon color="primary" @click="pasteAOIs" v-bind="attrs" v-on="on"><v-icon>{{icons.mdiContentPaste}}</v-icon></v-btn>
            </template>
            <span>Paste Copied AOIs (Ctrl+V)</span>
        </v-tooltip>

        <!-- Actions -->
        <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
                <v-btn color="error" class="square-btn mx-2" @click="showImport = true" v-bind="attrs" v-on="on"><v-icon>{{ icons.mdiFileDownloadOutline }}</v-icon></v-btn>
            </template>
            <span>Import</span>
        </v-tooltip>

        <v-spacer></v-spacer>
        
        <!-- Drawing Actions -->
        <v-btn color="warning" class="mr-2 square-btn" @click="undoDraw" v-if="currentMode === 'polygon'" :disabled="currentMode !== 'polygon' || points.length === 0"><v-icon>{{icons.mdiUndo}}</v-icon></v-btn>
        
         <!-- Export Menu -->
        <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
            <v-btn color="success" v-bind="attrs" v-on="on" class="square-btn mr-2"><v-icon>{{ icons.mdiDownload }}</v-icon></v-btn>
            </template>
            <v-list>
                <v-list-item @click="exportCanvas('heatmap')"><v-list-item-title>Image + Heatmap</v-list-item-title></v-list-item>
                <v-list-item @click="exportCanvas('aoi')"><v-list-item-title>Image + AOIs</v-list-item-title></v-list-item>
                <v-list-item @click="exportCanvas('scanpath')"><v-list-item-title>Image + Scanpath</v-list-item-title></v-list-item>
                <v-divider></v-divider>
                <v-tooltip bottom :disabled="!isMetMedia">
                    <template v-slot:activator="{ on, attrs }">
                        <div v-bind="attrs" v-on="on">
                            <v-list-item @click="exportVideo()" :disabled="isMetMedia">
                                <v-list-item-title>Video: Heatmap Progression</v-list-item-title>
                            </v-list-item>
                        </div>
                    </template>
                    <span>Video export capabilities only for non MET media files</span>
                </v-tooltip>
            </v-list>
        </v-menu>
      </div>

      <!-- Sliders -->
      <v-row class="mb-2">
         <v-col cols="12" md="3"><div class="d-flex align-center"><v-slider v-model="zoomLevel" label="Zoom" min="10" max="300" thumb-label hide-details></v-slider></div></v-col>
         <v-col cols="12" md="3"><div class="d-flex align-center"><v-slider v-model="heatmapOpacity" @input="opacityChange" label="Opacity" min="0.1" max="1" step="0.1" thumb-label hide-details></v-slider></div></v-col>
         <v-col cols="12" md="3"><div class="d-flex align-center"><v-slider v-model="heatmapRadius" @input="updateHeatmapVisualization" label="Radius" min="1" max="100" thumb-label hide-details></v-slider></div></v-col>
         <v-col cols="12" md="3">
            <div class="d-flex align-center">
                <v-range-slider 
                    v-model="timeRangeSeconds" 
                    @input="updateHeatmapVisualization" 
                    :min="minTimeSeconds" 
                    :max="maxTimeSeconds" 
                    step="0.1" 
                    label="Time (s)" 
                    thumb-label 
                    hide-details>
                </v-range-slider>
            </div>
        </v-col>
      </v-row>
    </v-card-text>


    <!-- Scanpath Controls -->
    <v-card-actions class="flex-column align-stretch pa-4" v-if="showScanpath">
        
        <v-slider
            v-model="currentIndex"
            :max="totalRespondents - 1"
            min="0"
            step="1"
            thumb-label
            color="primary"
            track-color="grey-lighten-2"
            hide-details
            class="w-100 mb-2"
        >
            <template v-slot:prepend>
                <span class="text-caption text-grey">1</span>
            </template>
            <template v-slot:append>
                <span class="text-caption text-grey">{{ totalRespondents }}</span>
            </template>
        </v-slider>

        <div class="d-flex justify-space-between w-100">
            <v-btn 
                variant="tonal" 
                color="primary" 
                @click="prevRespondent" 
                :disabled="currentIndex === 0"
                prepend-icon="mdi-arrow-left"
            >
                Previous
            </v-btn>

            <div class="text-body-2 align-self-center text-grey-darken-1">
                Respondent: <strong>{{ currentRespondentName }}</strong> {{ currentIndex + 1 }} / {{ totalRespondents }}
            </div>

            <v-btn 
                variant="tonal" 
                color="primary" 
                @click="nextRespondent" 
                :disabled="currentIndex === totalRespondents - 1"
                append-icon="mdi-arrow-right"
            >
                Next
            </v-btn>
        </div>
    </v-card-actions>
    <v-card-text class="image-workspace-area fill-height" style="position: relative; overflow: hidden;">
    
        <v-navigation-drawer
        v-model="aoiDrawer"
        absolute
        width="300"
        class="elevation-10"
        style="z-index: 10;"
        >
            <v-list dense class="aoi-list-content">
                <v-subheader>
                    Defined AOIs
                    <v-spacer></v-spacer>
                    <!-- Close button for the drawer -->
                     <v-btn icon color="info" class="mr-2 square-btn" @click="groupZones" :disabled="selectedZoneIndices.length < 2"><v-icon>{{icons.mdiGroup}}</v-icon></v-btn>
        
                    <v-btn icon small @click="aoiDrawer = false"><v-icon>{{icons.mdiClose}}</v-icon></v-btn>
                </v-subheader>
                
                <v-list-item
                    v-for="(zone, index) in zones"
                    :key="index"
                    :class="{ 'selected': selectedZoneIndices.includes(zone.id) }"
                    @click.exact="selectZone(zone.id)"
                    @click.shift.exact="selectZone(zone.id, true)"
                    @click.ctrl.exact="selectZone(zone.id, true)"
                    @click.meta.exact="selectZone(zone.id, true)"
                    :style="{ color: zone.color }"
                >
                    <v-list-item-content><v-list-item-title>{{ zone.name }}</v-list-item-title></v-list-item-content>
                    <v-list-item-action class="d-flex flex-row">
                        <v-btn icon x-small @click.stop="openEditLabelDialog(index)"><v-icon>{{icons.mdiPencil}}</v-icon></v-btn>
                        <v-btn icon x-small @click.stop="openDeleteConfirmDialog(index)"><v-icon color="error">{{icons.mdiDelete}}</v-icon></v-btn>
                    </v-list-item-action>
                </v-list-item>
            </v-list>
            <v-list dense class="aoi-list-content" v-if="groups.length > 0">
                <v-subheader>Groups/Nets</v-subheader>
                <v-list-item
                    v-for="(zone, index) in groups"
                    :key="index"
                    @click="selectGroup(index)"
                    :class="{ 'selected': selectedGroupIndex == index }"
                >
                    <v-list-item-content>
                        <v-list-item-title>
                            {{ zone.name }}
                        </v-list-item-title>
                        <template v-if="zone.aois.length > 0">
                            <v-list-item-subtitle v-for="aoi, aoiIdx in zone.aois">
                                {{(aoiIdx+1)+". "+zones.find(i => i.id == aoi).name }}
                            </v-list-item-subtitle>
                        </template>
                    </v-list-item-content>
                    <v-list-item-action class="d-flex flex-row">
                        <v-btn icon x-small @click.stop="openEditLabelDialog(index, group=true)">
                            <v-icon>{{icons.mdiPencil}}</v-icon>
                        </v-btn>
                        <v-btn icon x-small @click.stop="openDeleteNetConfirmDialog(index)">
                            <v-icon color="error">{{icons.mdiDelete}}</v-icon>
                        </v-btn>
                    </v-list-item-action>
                </v-list-item>
            </v-list>
        </v-navigation-drawer>

        <v-btn
        v-if="!aoiDrawer"
        absolute
        top
        left
        small
        fab
        color="white"
        class="mt-2 ml-2"
        style="z-index: 5"
        @click="aoiDrawer = true"
        >
            <v-icon>{{icons.mdiFormatListBulleted}}</v-icon>
        </v-btn>

        <!-- 4. Viewport -->
        <div class="image-canvas-viewport-col fill-height"
            :style="{ 
                paddingLeft: aoiDrawer ? '300px' : '0px',
                transition: 'padding-left 0.2s cubic-bezier(0.25, 0.8, 0.5, 1)' 
            }"
        >
        <div class="image-canvas-scroll-container"  ref="scrollContainer">
            <div class="image-container stack-context" :style="zoomContainerStyle" ref="zoomContainer">
                <!-- Layers -->
                <v-img 
                    :src="selectedItem.media" 
                    ref="eftImage" 
                    @load="onImageLoad"
                    class="layer-base"
                    :class="{ 'grayscale-effect': isGrayscale }"
                    crossorigin="anonymous"
                    :width="workingWidth"
                    :height="workingHeight"
                />
                
                <div id="heatmap-container" ref="heatmapContainer" class="layer-overlay" :style="{ visibility: showHeatmap ? 'visible' : 'hidden' }"></div>
                <canvas 
                    id="drawing-canvas" 
                    ref="drawingCanvas" 
                    :class="currentMode"
                    @mousedown="handleMouseDown"
                    @mousemove="handleMouseMove"
                    @mouseup="handleMouseUp"
                    :style="{ visibility: showAOIs ? 'visible' : 'hidden' }"
                ></canvas>

                <!-- Scanpath Overlay Container -->
                <svg 
                    id="scanpath-container" 
                    ref="scanpathContainer"
                    class="scanpath-svg" 
                    :viewBox="`0 0 ${workingWidth} ${workingHeight}`"
                    :style="{ width: workingWidth + 'px', height: workingHeight + 'px' }"
                    preserveAspectRatio="none"
                    v-if="showScanpath"
                >
                    <!-- Saccades (Lines) -->
                    <polyline 
                        :points="saccadePoints" 
                        fill="none" 
                        stroke="#1976D2" 
                        stroke-width="3" 
                        stroke-dasharray="8,4"
                        stroke-opacity="0.6"
                        stroke-linejoin="round"
                    />

                    <!-- Fixations (Circles) -->
                    <g v-for="(fix, idx) in currentFixations" :key="fix.index">
                        <circle 
                            :cx="fix.fix_x" 
                            :cy="fix.fix_y" 
                            :r="calculateRadius(fix.duration)" 
                            fill="rgba(33, 150, 243, 0.4)" 
                            stroke="#1976D2" 
                            stroke-width="2"
                            class="fixation-circle"
                        >
                            <title>Fixation {{ idx + 1 }}: {{ fix.duration }}ms</title>
                        </circle>
                        <!-- Sequence Number -->
                        <text 
                            :x="fix.fix_x" 
                            :y="fix.fix_y" 
                            dy=".35em" 
                            text-anchor="middle" 
                            class="fixation-text"
                        >
                            {{ idx + 1 }}
                        </text>
                    </g>
                    <!-- Start Arrow -->
                    <path
                        v-if="startArrowPath"
                        :d="startArrowPath"
                        fill="none"
                        stroke="#00E676" 
                        stroke-width="4"
                        stroke-linejoin="round"
                        stroke-linecap="round"
                        class="start-arrow"
                    />
                </svg>
                <div 
                    v-if="analysisLoading"
                    class="layer-overlay d-flex align-center justify-center"
                    style="z-index: 5; background-color: rgba(0, 0, 0, 0.4);"
                >
                    <v-progress-circular indeterminate color="white" size="64" width="6"></v-progress-circular>
                </div>
            </div>
        </div>
        </div>
    </v-card-text>

    <!-- Dialogs -->
    <v-dialog v-model="showEditLabelDialog" max-width="400px">
        <v-card>
            <v-card-title>Edit AOI</v-card-title>
            <v-card-text>
                <v-text-field v-model="editedZoneLabel" outlined dense autofocus hide-details @keydown.enter="saveEditedAOI" class="mb-2"></v-text-field>
                <v-select v-model="editedElement" :items="groupedAOIElements" item-text="name" item-value="id" label="Type" outlined dense hide-details :loading="elementsLoading"></v-select>
            </v-card-text>
            <v-card-actions>
                <v-btn text @click="cancelEditAOI">Cancel</v-btn>
                <v-btn color="primary" @click="saveEditedAOI" :loading="aoiLoading">Save</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>

    <v-dialog v-model="showDeleteConfirmDialog" max-width="400px">
        <v-card>
            <v-card-title>Delete AOI</v-card-title>
            <v-card-text>Are you sure you want to delete this AOI? This action cannot be undone.</v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer> 
                <v-btn text @click="cancelDeleteAoi">Cancel</v-btn>
                <v-btn color="error" @click="deleteZone" :loading="aoiLoading">Delete</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
    <v-dialog v-model="showDeleteNetConfirmDialog" max-width="400px">
        <v-card>
            <v-card-title>Delete Net</v-card-title>
            <v-card-text>Are you sure you want to delete this Net? This action cannot be undone.</v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer> 
                <v-btn text @click="cancelDeleteNet">Cancel</v-btn>
                <v-btn color="error" @click="deleteGroup" :loading="netLoading">Delete</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
     
     <v-snackbar v-model="isSnackbarVisible">{{ snackBarMessage }}</v-snackbar>
     
        <oculis-kpi-table-dialog
            v-if="selectedItem"
        v-model="showKPITable"
        :analysis-data="analysisData"
        :media-type="selectedItem.type"
        :summary-analysis-data="summaryAnalysisData"
        />
    
      <oculis-import-dialog
        v-model="showImport"
        :current-image="selectedItem"
        @aois-imported="aoisImport"
      />
      <!-- FAQ / Help Modal -->
    <v-dialog v-model="showHelpDialog" max-width="700px">
      <v-card>
        <v-card-title class="headline bg-primary text-white">
          Editor Help & Instructions
          <v-spacer></v-spacer>
          <v-btn icon dark @click="showHelpDialog = false">
            <v-icon>{{icons.mdiClose}}</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-card-text class="pa-4">
          
          <!-- Additional Resources Links -->
          <div class="d-flex align-center justify-center mb-5 pa-3 rounded" style="background-color: #f0f7ff; border: 1px solid #cce0ff;">
            <span class="mr-3 text-body-1">Need more details?</span>
            <v-btn 
              href="https://ipsosgroup.sharepoint.com/:f:/r/teams/ExtSP-BVA-PRSIV-GrandCentral/Shared Documents/5. TRAINING %26 GUIDES/Oculis/User Guide?csf=1&web=1&e=gdWW3b" 
              target="_blank" 
              color="primary" 
              small
              outlined
              class="mx-2"
            >
              User Guides
            </v-btn>
            <v-btn 
              href="https://ipsosgroup.sharepoint.com/:v:/t/ExtSP-BVA-PRSIV-GrandCentral/IQB4MY30Ek4TT5f6QuFiyiV-Ad3hrh6ZYlpMouhz9IjPNgI?referrer=Outlook.Win32&referrerScenario=email-linkwithoutembed" 
              target="_blank" 
              color="primary" 
              small
              outlined
              class="mx-2"
            >
              Video Training
            </v-btn>
          </div>

          <v-expansion-panels accordion multiple>
            
            <v-expansion-panel>
              <v-expansion-panel-header class="font-weight-bold">1. Drawing AOIs (Areas of Interest)</v-expansion-panel-header>
              <v-expansion-panel-content>
                <ul class="mb-0">
                  <li class="mb-2"><strong>Rectangle / Circle:</strong> Select the tool from the toolbar. Click and drag on the image to define the size. Release the mouse to save and name the AOI.</li>
                  <li><strong>Polygon:</strong> Select the polygon tool. Click on the image to add points. To close and save the shape, click near your starting point (a red circle indicates the start).</li>
                </ul>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel>
              <v-expansion-panel-header class="font-weight-bold">2. Selecting, Moving & Resizing</v-expansion-panel-header>
              <v-expansion-panel-content>
                <ul class="mb-0">
                  <li class="mb-2"><strong>Single Selection:</strong> Use the <em>Select/Edit (Pencil)</em> tool and click an AOI. Drag its edges/points to resize it, or drag from the center to move it.</li>
                  <li class="mb-2"><strong>Multi-Selection:</strong> Hold <strong>Shift</strong> or <strong>Ctrl / Cmd</strong> while clicking multiple AOIs.</li>
                  <li><strong>Multi-Resize:</strong> With multiple AOIs selected, dragging an edge/point of one AOI will scale all selected AOIs proportionally.</li>
                </ul>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel>
              <v-expansion-panel-header class="font-weight-bold">3. Copy, Paste & Shortcuts</v-expansion-panel-header>
              <v-expansion-panel-content>
                <ul class="mb-0">
                  <li class="mb-2"><strong>Copy:</strong> Select one or more AOIs and press <strong>Ctrl+C</strong> (or use the copy button in the toolbar).</li>
                  <li><strong>Paste:</strong> Press <strong>Ctrl+V</strong> (or use the paste button). Duplicated AOIs will appear with a slight offset and " copy" appended to their name. They are automatically saved.</li>
                </ul>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel>
              <v-expansion-panel-header class="font-weight-bold">4. Grouping AOIs (Creating Nets)</v-expansion-panel-header>
              <v-expansion-panel-content>
                <ul class="mb-0">
                  <li>To group multiple AOIs together into a <strong>Net</strong>, multi-select them (Shift+Click). Then open the left-side AOI drawer and click the <strong>Group</strong> icon <v-icon small>{{icons.mdiGroup}}</v-icon> at the top right of the drawer.</li>
                </ul>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel>
              <v-expansion-panel-header class="font-weight-bold">5. Heatmaps & Time Filtering</v-expansion-panel-header>
              <v-expansion-panel-content>
                <ul class="mb-0">
                  <li class="mb-2"><strong>Toggling:</strong> Use the flame icon in the top left to show/hide the heatmap overlay.</li>
                  <li class="mb-2"><strong>Sliders:</strong> Adjust Opacity and Radius sliders to customize the visual intensity.</li>
                  <li><strong>Time Range:</strong> Use the Time slider to filter fixations occurring between a specific start and end time. The heatmap and scanpath will update instantly.</li>
                </ul>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <v-expansion-panel>
              <v-expansion-panel-header class="font-weight-bold">6. Exports & Video Generation</v-expansion-panel-header>
              <v-expansion-panel-content>
                <ul class="mb-0">
                  <li class="mb-2"><strong>Static Exports:</strong> Click the Download menu to export an image combining your base media with the Heatmap, AOIs, or Scanpaths.</li>
                  <li><strong>Video Export:</strong> Under the Download menu, select "Video: Heatmap Progression" to generate an MP4/WebM of your heatmap organically forming over the maximum recorded time. <em>(Note: Video export is not available for MET media files).</em> If KPI Overlays are active, the numbers will also climb organically in the video.</li>
                </ul>
              </v-expansion-panel-content>
            </v-expansion-panel>

          </v-expansion-panels>
        </v-card-text>
        <v-card-actions class="pb-4 pr-4">
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="showHelpDialog = false">Got it</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { ref, watch, onMounted, onBeforeUnmount, computed } from '@vue/composition-api'
import OculisKpiTableDialog from '../oculis-kpi/OculisKpiTableDialog.vue'
import OculisImportDialog from '../oculis-dialogs/OculisImportDialog.vue'
import h337 from 'heatmapjs/heatmap.js'
import useOculis from '../../useOculis'

// Icons import
import {
    mdiVectorRectangle, mdiVectorPolygon, mdiVectorCircle, mdiCursorMove, mdiPencil,
    mdiPlus, mdiContentSaveOutline, mdiUndo, mdiGroup, mdiDelete, mdiFileDownloadOutline,
    mdiFire, mdiDrawingBox, mdiInvertColors, mdiChartBar, mdiTableLarge, mdiDownload,
    mdiClose, mdiFormatListBulleted, mdiMapMarkerPath, mdiContentCopy, mdiContentPaste,
    mdiHelpCircleOutline
} from '@mdi/js'

export default {
  components: {
    OculisKpiTableDialog,
    OculisImportDialog
  },
  props: {
    selectedItem: { type: Object, required: true },
    medias: { type: Array, default: () => [] }
  },
  setup(props) {
    const { 
        heatmapData, heatmapLoading, fetchMediaHeatmap, 
        analysisData, summaryAnalysisData, analysisLoading, fetchMediaAnalysis,
        aoiLoading, bulkCreateAOIs, addAOI, updateAOI, deleteAOI,
        netLoading, addNet, updateNet, deleteNet,
        mediaData, mediasLoading, fetchMedia, generateGroupId,
        fetchScanpath, scanpathData, scanpathLoading,
        elementsLoading, elementsOptions, fetchAOIElements,
    } = useOculis()

    // --- State ---
    const scrollContainer = ref(null)
    const drawingCanvas = ref(null)
    const aoiDrawer = ref(true)
    const eftImage = ref(null)
    const heatmapContainer = ref(null)
    const scanpathContainer = ref(null)
    let ctx = null
    let heatmapInstance = null

    // UI Toggles
    const showHeatmap = ref(false)
    const showAOIs = ref(true)
    const showKPITable = ref(false)
    const showScanpath = ref(false)
    const showImport = ref(false)
    const isGrayscale = ref(false)
    const showKPIs = ref(false)
    const zoomLevel = ref(100)
    const showHelpDialog = ref(false)
    
    // Data
    const zones = ref([])
    const groups = ref([])
    const points = ref([])
    const selectedZoneIndices = ref([])
    const selectedZoneIndex = ref(null)
    const selectedGroupIndex = ref(null)
    const clipboardAOIs = ref([]) 
    
    // Drawing
    const currentMode = ref('edit-points')
    const drawing = ref(false)
    const currentShape = ref(null)
    const dragStart = ref(null)
    const draggingZoneId = ref(null)
    const draggingPointIndex = ref(null)
    const originalDragState = ref(null)

    // Filtering & Video State
    const minTime = ref(0)
    const maxTime = ref(10000)
    const timeRange = ref([0, 10000])
    const isRecording = ref(false)
    const recordingProgress = ref(0)

    // --- Scaling State ---
    const MAX_CANVAS_WIDTH = 15000;
    const MAX_CANVAS_HEIGHT = 4000;
    const workingWidth = ref(0);
    const workingHeight = ref(0);
    const dataScale = ref(1); // Ratio to scale data down for rendering
    

    const modeOptions = [
        {label: 'Select/Edit', value:'edit-points', icon:mdiPencil},
        {label: 'Move', value:'move', icon:mdiCursorMove},
        {label: 'Rectangle', value:'rectangle', icon:mdiVectorRectangle},
        {label: 'Circle', value:'circle', icon:mdiVectorCircle},
        {label: 'Polygon', value:'polygon', icon:mdiVectorPolygon},
    ]
    const zoneColor = ref('#ff0000')

    const colorPalette = [
        '#FF6347', '#4682B4', '#32CD32', '#FFD700', '#8A2BE2', 
        '#FF69B4', '#00CED1', '#FF4500', '#ADFF2F', '#BA55D3'
    ];
    const nextColorIndex = ref(0);
    const getNextColor = () => {
        const color = colorPalette[nextColorIndex.value];
        nextColorIndex.value = (nextColorIndex.value + 1) % colorPalette.length;
        return color;
    };

    
    // Scanpath config
    const hasLoadedScanpath = ref(false);

    // Heatmap config
    const hasLoadedHeatmap = ref(false);
    const heatmapScale = ref(1); // Used to shrink the canvas memory footprint
    let heatmapUpdateTimeout = null; // Used to debounce the slider
    const heatmapOpacity = ref(0.6)
    const heatmapRadius = ref(25)
    const heatmapConfig = ref({
        minOpacity: 0.0001,
        maxOpacity: .78,
        blur: 1,
        gradient: { '0': 'blue', '.4': 'green', '.5': 'green', '.7': 'yellow', '.9': 'red' }
    })

    // Scaling
    const imageNaturalWidth = ref(0)
    const imageNaturalHeight = ref(0)
    const scaleX = ref(1) 
    const scaleY = ref(1)

    // KPI
    const selectedKpiMetric = ref(['order', 'noting', 'duration']);
    const kpiMetricsOptions = [
            {text:'Noting', value:'noting'}, 
            {text:'Visit Duration', value:'duration'},
            {text:'Order', value:'order'},
            {text:'% Duration', value:'duration_percent'},
    ]

    // Dialogs / Misc
    const showEditLabelDialog = ref(false)
    const editedZoneLabel = ref('')
    const editedElement = ref('')
    const editingZoneIndex = ref(null)
    const showDeleteConfirmDialog = ref(false);
    const showDeleteNetConfirmDialog = ref(false)
    const isSnackbarVisible = ref(false)
    const snackBarMessage = ref('')

    // --- Computed Properties ---
    const isMetMedia = computed(() => {
        return props.selectedItem?.source === 2 || mediaData.value?.source === 2;
    });
    // Zoom Transformation
    const zoomContainerStyle = computed(() => {
        const style = {
            transform: `scale(${zoomLevel.value / 100})`,
            transformOrigin: 'top left',
        };
        
        // Only apply hard dimensions AFTER the image has loaded and calculated them
        if (workingWidth.value > 0 && workingHeight.value > 0) {
            style.width = `${workingWidth.value}px`;
            style.height = `${workingHeight.value}px`;
        }
        
        return style;
    });

    // Time Slider UI Computation (Convert MS logic to Seconds logic for UI)
    const timeRangeSeconds = computed({
        get: () => [timeRange.value[0] / 1000, timeRange.value[1] / 1000],
        set: (val) => { timeRange.value = [val[0] * 1000, val[1] * 1000]; }
    })
    const minTimeSeconds = computed(() => minTime.value / 1000)
    const maxTimeSeconds = computed(() => maxTime.value / 1000)


    // --- Core Methods ---
    const onImageLoad = async () => {
        if(!eftImage.value || !eftImage.value.image) return;
        
        imageNaturalWidth.value = eftImage.value.image.naturalWidth;
        imageNaturalHeight.value = eftImage.value.image.naturalHeight;
        
        // 1. Calculate structural scale to fit within MAX limits
        const scaleW = MAX_CANVAS_WIDTH / imageNaturalWidth.value;
        const scaleH = MAX_CANVAS_HEIGHT / imageNaturalHeight.value;
        dataScale.value = Math.min(1, scaleW, scaleH); // Only scale down, never up

        // 2. Set working dimensions
        workingWidth.value = Math.round(imageNaturalWidth.value * dataScale.value);
        workingHeight.value = Math.round(imageNaturalHeight.value * dataScale.value);
        
        // 3. Zoom calculation based on working dimensions
        if (scrollContainer.value) {
            const availableWidth = scrollContainer.value.clientWidth - 24;
            const scaleW = availableWidth / workingWidth.value;
            const scale = Math.min(scaleW, 1);
            zoomLevel.value = scale < 1 ? Math.floor(scale * 100) : 100;
        }
        
        // 4. Set Canvas sizes to WORKING dimensions
        if(drawingCanvas.value) {
            drawingCanvas.value.width = workingWidth.value;
            drawingCanvas.value.height = workingHeight.value;
            ctx = drawingCanvas.value.getContext('2d', { willReadFrequently: true }); 
        }
        if(heatmapContainer.value) {
            heatmapContainer.value.style.width = `${workingWidth.value}px`;
            heatmapContainer.value.style.height = `${workingHeight.value}px`;
        }

        const maxDim = Math.max(workingWidth.value, workingHeight.value);
        heatmapRadius.value = Math.max(20, Math.min(Math.round((maxDim / 1500) * 25), 100));

        initHeatmap();
        
        if(props.selectedItem.id) {
            await fetchMedia({id: props.selectedItem.id});
            
            // Removed fetchMediaHeatmap and fetchScanpath from here to lazy-load them!
            await fetchMediaAnalysis(mediaData.value.project, mediaData.value.id);

            // 5. Scale incoming AOIs DOWN to match the working canvas
            const scalePoint = (p) => ({ x: p.x * dataScale.value, y: p.y * dataScale.value });
            const aois = mediaData.value.aois.map(aoi => {
                const scaled = { ...aoi, project: mediaData.value.project };
                if (scaled.shape === 'rectangle') {
                    scaled.x *= dataScale.value; scaled.y *= dataScale.value;
                    scaled.width *= dataScale.value; scaled.height *= dataScale.value;
                } else if (scaled.shape === 'circle') {
                    scaled.x *= dataScale.value; scaled.y *= dataScale.value;
                    scaled.radius *= dataScale.value;
                } else if (scaled.shape === 'polygon') {
                    scaled.points = scaled.points.map(scalePoint);
                }
                return scaled;
            });

            zones.value = aois ? JSON.parse(JSON.stringify(aois)) : [];
            groups.value = mediaData.value.nets ? JSON.parse(JSON.stringify(mediaData.value.nets)) : [];
            redrawAllZones();
        }
    }
    
    const initHeatmap = () => {
        if (!heatmapContainer.value) return;

        // Cap the maximum canvas dimension to 4096px to prevent crashes
        const MAX_DIMENSION = 4096; 
        const maxImageDim = Math.max(imageNaturalWidth.value, imageNaturalHeight.value);
        
        heatmapScale.value = maxImageDim > MAX_DIMENSION ? MAX_DIMENSION / maxImageDim : 1;
        const hmScale = heatmapScale.value;

        // Make the physical canvas smaller, but use CSS transform to scale it up visually
        heatmapContainer.value.style.width = `${imageNaturalWidth.value * hmScale}px`;
        heatmapContainer.value.style.height = `${imageNaturalHeight.value * hmScale}px`;
        heatmapContainer.value.style.transform = `scale(${1 / hmScale})`;
        heatmapContainer.value.style.transformOrigin = 'top left';

        const originalGetContext = HTMLCanvasElement.prototype.getContext;
        HTMLCanvasElement.prototype.getContext = function(type, attributes) {
            if (type === '2d') attributes = { ...attributes, willReadFrequently: true };
            return originalGetContext.call(this, type, attributes);
        };
        try {
            heatmapInstance = h337.create({
                container: heatmapContainer.value,
                radius: heatmapRadius.value * hmScale, // Scale radius
                maxOpacity: heatmapOpacity.value,
                ...heatmapConfig.value
            });
        } finally {
            HTMLCanvasElement.prototype.getContext = originalGetContext;
        }
    }

    const extractTimeBounds = () => {
        let min = Infinity;
        let max = -Infinity;
        let foundTime = false;

        const evaluateTime = (s, d, e) => {
            if (s !== undefined && s !== null) {
                foundTime = true;
                if (s < min) min = s;
                if (e !== undefined && e !== null && e > max) max = e;
                else if (s > max) max = s;
            }
        };

        if (heatmapData.value?.data) {
            heatmapData.value.data.forEach(p => {
                const s = p.start ?? p.start_time ?? p.fix_start ?? p.timestamp;
                const d = p.duration ?? 0;
                const e = p.end ?? p.end_time ?? p.fix_end ?? (s !== undefined ? s + d : undefined);
                evaluateTime(s, d, e);
            });
        }

        if (!foundTime && scanpathData.value?.respondents) {
            scanpathData.value.respondents.forEach(r => {
                (r.fixations || []).forEach(f => {
                    const s = f.start ?? f.start_time ?? f.fix_start ?? f.timestamp;
                    const d = f.duration ?? 0;
                    const e = f.end ?? f.end_time ?? f.fix_end ?? (s !== undefined ? s + d : undefined);
                    evaluateTime(s, d, e);
                });
            });
        }

        if (foundTime && max > -Infinity) {
            minTime.value = Math.floor(min);
            maxTime.value = Math.ceil(max);
            timeRange.value = [minTime.value, maxTime.value];
        } else {
            minTime.value = 0; maxTime.value = 10000;
            timeRange.value = [0, 10000];
        }
    }

    const updateHeatmapVisualization = () => {
        clearTimeout(heatmapUpdateTimeout);
        heatmapUpdateTimeout = setTimeout(() => {
            if (!heatmapInstance) return;

            const tMin = timeRange.value[0];
            const tMax = timeRange.value[1];
            const hmScale = heatmapScale.value;
            const scaledRadius = Math.max(1, heatmapRadius.value * hmScale);

            let filteredData = [];
            const hasTime = heatmapData.value?.data?.some(p => (p.start ?? p.start_time ?? p.fix_start ?? p.timestamp) !== undefined);

            if (hasTime) {
                const dataArr = heatmapData.value.data || [];
                // OPTIMIZATION: Single standard for-loop is much faster than filter.map
                for (let i = 0; i < dataArr.length; i++) {
                    const p = dataArr[i];
                    let s = p.start ?? p.start_time ?? p.fix_start ?? p.timestamp;
                    let e = p.end ?? p.end_time ?? p.fix_end ?? (s + (p.duration || 0));
                    
                    if (s <= tMax && e >= tMin) {
                        filteredData.push({
                            x: Math.floor(p.x * hmScale),
                            y: Math.floor(p.y * hmScale),
                            value: p.value || p.duration || 1,
                            radius: scaledRadius
                        });
                    }
                }
            }  else if (scanpathData.value?.respondents) {
                scanpathData.value.respondents.forEach(resp => {
                    (resp.fixations || []).forEach(fix => {
                        let s = fix.start ?? fix.start_time ?? fix.fix_start ?? fix.timestamp;
                        let e = fix.end ?? fix.end_time ?? fix.fix_end ?? (s + (fix.duration || 0));
                        
                        // Only push if within the selected time boundaries
                        if (s === undefined || (s <= tMax && e >= tMin)) {
                            filteredData.push({
                                x: fix.fix_x,
                                y: fix.fix_y,
                                value: fix.duration || 1,
                                radius: heatmapRadius.value
                            });
                        }
                    });
                });
            } else {
                filteredData = (heatmapData.value?.data || []).map(p => ({ ...p, radius: heatmapRadius.value }));
            }

            if (filteredData.length > 0) {
                updateHeatMinMax(filteredData, imageNaturalWidth.value * hmScale, imageNaturalHeight.value * hmScale, heatmapInstance);
            }

            heatmapInstance.setData({
                max: 2048 || 100,
                min: heatmapData.value?.min || 0,
                data: filteredData
            });
        }, 50);
    }

    const updateHeatMinMax = (data, width, height, heatmap) => {
        if (!data.length) return;

        // Use a flat TypedArray for binning instead of expensive JS objects/strings
        const cols = 100;
        const rows = 100;
        const bins = new Float32Array(cols * rows);
        
        const xBinSize = Math.max(1, width / cols);
        const yBinSize = Math.max(1, height / rows);
        
        // Fast O(N) mapping
        for (let i = 0; i < data.length; i++) {
            const c = data[i];
            const col = Math.min(cols - 1, Math.max(0, Math.floor(c.x / xBinSize)));
            const row = Math.min(rows - 1, Math.max(0, Math.floor(c.y / yBinSize)));
            
            bins[row * cols + col] += c.value; 
        }
        
        // Extract non-zero bins
        const activeBins = [];
        for (let i = 0; i < bins.length; i++) {
            if (bins[i] > 0) activeBins.push(bins[i]);
        }
        
        if (activeBins.length === 0) return;
        
        // Calculate Percentiles
        activeBins.sort((a, b) => a - b);
        const index02 = Math.floor(activeBins.length * 0.02);
        const index98 = Math.floor(activeBins.length * 0.98) || (activeBins.length - 1);
        
        heatmap.setDataMax(activeBins[index98]);
        heatmap.setDataMin(activeBins[index02]);
    }

    const convertToNatural = (renderedX, renderedY) => ({ x: renderedX * scaleX.value, y: renderedY * scaleY.value });
    const convertToRendered = (naturalX, naturalY) => ({ x: naturalX / scaleX.value, y: naturalY / scaleY.value });

    // 2. Drawing / Redrawing
    const redrawAllZones = () => {
        if(!ctx) return;
        ctx.clearRect(0, 0, drawingCanvas.value.width, drawingCanvas.value.height)
        
        zones.value.forEach((zone, idx) => {
             const isSel = selectedZoneIndices.value.includes(zone.id) || selectedZoneIndex.value === zone.id
             drawZoneShape(zone, isSel)
             if(showKPIs.value) drawKpiOverlay(zone)
        })
        
        if(currentMode.value === 'polygon' && points.value.length > 0) {
            ctx.beginPath();
            points.value.forEach((p, i) => { i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y) });
            ctx.strokeStyle = 'red'; ctx.lineWidth = 2; ctx.stroke();
            const startP = points.value[0];
            ctx.beginPath(); ctx.arc(startP.x, startP.y, 5, 0, 2 * Math.PI); ctx.fillStyle = 'red'; ctx.fill();
        }
    }

    const drawZoneShape = (zone, isSelected=false) => {
        ctx.fillStyle = zone.color;
        ctx.strokeStyle = isSelected ? 'blue' : zone.color;
        ctx.lineWidth = isSelected ? 4 : 2;
        ctx.globalAlpha = isSelected ? 0.7 : zone.opacity || 0.5;

        ctx.beginPath();
        if(zone.shape === 'rectangle') { ctx.rect(zone.x, zone.y, zone.width, zone.height);
        } else if (zone.shape === 'circle') { ctx.arc(zone.x, zone.y, zone.radius, 0, 2 * Math.PI);
        } else if (zone.shape === 'polygon') { zone.points.forEach((p, i) => { i===0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y) }); ctx.closePath(); }
        ctx.fill(); ctx.stroke(); ctx.globalAlpha = 1;

        if (isSelected && currentMode.value === 'edit-points') {
            if(zone.shape === 'rectangle') { drawPoint(zone.x, zone.y); drawPoint(zone.x + zone.width, zone.y); drawPoint(zone.x, zone.y + zone.height); drawPoint(zone.x + zone.width, zone.y + zone.height);
            } else if (zone.shape === 'circle') { drawPoint(zone.x + zone.radius, zone.y); drawPoint(zone.x - zone.radius, zone.y); drawPoint(zone.x, zone.y + zone.radius); drawPoint(zone.x, zone.y - zone.radius);
            } else if (zone.shape === 'polygon') { zone.points.forEach((p) => drawPoint(p.x, p.y)); }
        }
    }
    const drawPoint = (x, y) => { if (!ctx) return; ctx.fillStyle = 'blue'; ctx.beginPath(); ctx.arc(x, y, 4, 0, 2 * Math.PI); ctx.fill(); }

    // 3. Mouse Handlers
    const handleMouseDown = (e) => {
        const canvas = drawingCanvas.value; const ctxVal = ctx; 
        const mode = currentMode.value; const zoom = zoomLevel.value;
        if (!canvas || !ctxVal) return;

        const rect = canvas.getBoundingClientRect();
        const xRendered = e.clientX - rect.left; const yRendered = e.clientY - rect.top;
        const zoomFactor = zoom / 100;
        const xCanvas = xRendered / zoomFactor; const yCanvas = yRendered / zoomFactor;
        const { x: xNatural, y: yNatural } = convertToNatural(xCanvas, yCanvas);

        const HIT_THRESHOLD_SQ = 25; 
        const isHit = (x1, y1, x2, y2) => ((x1 - x2) ** 2 + (y1 - y2) ** 2) <= HIT_THRESHOLD_SQ;

        if (mode === 'edit-points' && selectedZoneIndices.value.length > 0) {
            let foundZoneId = null; let foundPointIndex = -1;
            for (let i = selectedZoneIndices.value.length - 1; i >= 0; i--) {
                const id = selectedZoneIndices.value[i];
                const zone = zones.value.find(item => item.id === id);
                if (!zone) continue;

                if (zone.shape === 'polygon') {
                    for (let j = 0; j < zone.points.length; j++) {
                        const pRendered = convertToRendered(zone.points[j].x, zone.points[j].y);
                        if (isHit(xCanvas, yCanvas, pRendered.x, pRendered.y)) { foundPointIndex = j; break; }
                    }
                } else if (zone.shape === 'rectangle') {
                    const coords = [ { x: zone.x, y: zone.y }, { x: zone.x + zone.width, y: zone.y }, { x: zone.x, y: zone.y + zone.height }, { x: zone.x + zone.width, y: zone.y + zone.height } ];
                    for (let j = 0; j < 4; j++) {
                        const pRendered = convertToRendered(coords[j].x, coords[j].y);
                        if (isHit(xCanvas, yCanvas, pRendered.x, pRendered.y)) { foundPointIndex = j; break; }
                    }
                } else if (zone.shape === 'circle') {
                    const coords = [ { x: zone.x + zone.radius, y: zone.y }, { x: zone.x - zone.radius, y: zone.y }, { x: zone.x, y: zone.y + zone.radius }, { x: zone.x, y: zone.y - zone.radius } ];
                    for (let j = 0; j < 4; j++) {
                        const pRendered = convertToRendered(coords[j].x, coords[j].y);
                        if (isHit(xCanvas, yCanvas, pRendered.x, pRendered.y)) { foundPointIndex = j; break; }
                    }
                }

                if (foundPointIndex !== -1) { foundZoneId = zone.id; break; }
            }

            if (foundZoneId !== null) {
                draggingZoneId.value = foundZoneId; draggingPointIndex.value = foundPointIndex; dragStart.value = { x: xCanvas, y: yCanvas };
                if (selectedZoneIndices.value.length > 1) {
                    originalDragState.value = JSON.parse(JSON.stringify(zones.value.filter(z => selectedZoneIndices.value.includes(z.id))));
                }
                return; 
            }
        }

        if (mode === 'move' || mode === 'edit-points') {
            const foundIndex = findZoneAtCoordinates(xNatural, yNatural);
            if (foundIndex !== null) {
                if (e.shiftKey || e.ctrlKey || e.metaKey) {
                    const idx = selectedZoneIndices.value.indexOf(foundIndex);
                    if (idx === -1) selectedZoneIndices.value.push(foundIndex);
                    else selectedZoneIndices.value.splice(idx, 1);
                    selectedZoneIndex.value = selectedZoneIndices.value.length > 0 ? selectedZoneIndices.value[selectedZoneIndices.value.length - 1] : null;
                } else {
                    if (!selectedZoneIndices.value.includes(foundIndex)) {
                        selectedZoneIndex.value = foundIndex; selectedZoneIndices.value = [foundIndex];
                    }
                }
            } else {
                if (!(e.shiftKey || e.ctrlKey || e.metaKey)) { selectedZoneIndex.value = null; selectedZoneIndices.value = []; }
            }
            redrawAllZones(); 
        }

        if (mode === 'move' && selectedZoneIndices.value.length > 0) { dragStart.value = { x: xCanvas, y: yCanvas }; return; }

        drawing.value = true;
        const opacity = 0.5;
        switch (mode) {
            case 'rectangle': zoneColor.value = getNextColor(); currentShape.value = { shape: 'rectangle', x: xNatural, y: yNatural, width: 0, height: 0, color: zoneColor.value, opacity }; break;
            case 'circle': zoneColor.value = getNextColor(); currentShape.value = { shape: 'circle', x: xNatural, y: yNatural, radius: 0, color: zoneColor.value, opacity }; break;
            case 'polygon':
                if (points.value.length >= 3) {
                    const startPoint = points.value[0]; const startRendered = convertToRendered(startPoint.x, startPoint.y);
                    const distSq = (xCanvas - startRendered.x) ** 2 + (yCanvas - startRendered.y) ** 2;
                    if (isHit(xCanvas, yCanvas, startRendered.x, startRendered.y) || distSq <= 100) { addZone(); drawing.value = false; break; }
                }
                if (points.value.length === 0) zoneColor.value = getNextColor();
                points.value.push({ x: xNatural, y: yNatural });
                currentShape.value = { shape: 'polygon', points: points.value, color: zoneColor.value, opacity };
                redrawAllZones(); 
                break;
        }
    };

    const handleMouseMove = (e) => {
        const canvas = drawingCanvas.value; const ctxVal = ctx; 
        const mode = currentMode.value; const zoom = zoomLevel.value;
        if (!canvas || !ctxVal) return;

        const rect = canvas.getBoundingClientRect();
        const xRendered = e.clientX - rect.left; const yRendered = e.clientY - rect.top;
        const zoomFactor = zoom / 100;
        const xCanvas = xRendered / zoomFactor; const yCanvas = yRendered / zoomFactor;
        const { x: xNatural, y: yNatural } = convertToNatural(xCanvas, yCanvas);

        if (mode === 'move' && selectedZoneIndices.value.length > 0 && dragStart.value) {
            const start = dragStart.value;
            const dxNatural = (xCanvas - start.x) * scaleX.value; const dyNatural = (yCanvas - start.y) * scaleY.value;

            selectedZoneIndices.value.forEach(id => {
                const zone = zones.value.find(item => item.id === id);
                if (zone) {
                    if (zone.shape === 'polygon') {
                        for (let i = 0; i < zone.points.length; i++) { zone.points[i].x += dxNatural; zone.points[i].y += dyNatural; }
                    } else { zone.x += dxNatural; zone.y += dyNatural; }
                }
            });

            dragStart.value = { x: xCanvas, y: yCanvas };
            redrawAllZones();
            return;
        }

        if (mode === 'edit-points' && draggingZoneId.value !== null && draggingPointIndex.value !== null && dragStart.value !== null) {
            if (selectedZoneIndices.value.length === 1) {
                const zone = zones.value.find(item => item.id == draggingZoneId.value);
                const pointIdx = draggingPointIndex.value;

                if (zone) {
                    if (zone.shape === 'polygon') { zone.points[pointIdx].x = xNatural; zone.points[pointIdx].y = yNatural; } 
                    else if (zone.shape === 'rectangle') {
                        const ox = zone.x, oy = zone.y, ow = zone.width, oh = zone.height;
                        switch (pointIdx) {
                            case 0: zone.x = xNatural; zone.y = yNatural; zone.width = (ox + ow) - xNatural; zone.height = (oy + oh) - yNatural; break;
                            case 1: zone.y = yNatural; zone.width = xNatural - ox; zone.height = (oy + oh) - yNatural; break;
                            case 2: zone.x = xNatural; zone.width = (ox + ow) - xNatural; zone.height = yNatural - oy; break;
                            case 3: zone.width = xNatural - ox; zone.height = yNatural - oy; break;
                        }
                        if (zone.width < 0) { zone.x += zone.width; zone.width = -zone.width; }
                        if (zone.height < 0) { zone.y += zone.height; zone.height = -zone.height; }
                    } 
                    else if (zone.shape === 'circle') {
                        if (pointIdx === 0) zone.radius = Math.abs(xNatural - zone.x);
                        else if (pointIdx === 1) zone.radius = Math.abs(zone.x - xNatural);
                        else if (pointIdx === 2) zone.radius = Math.abs(yNatural - zone.y);
                        else if (pointIdx === 3) zone.radius = Math.abs(zone.y - yNatural);
                    }
                }
            } 
            else if (originalDragState.value) {
                const activeOrigZone = originalDragState.value.find(z => z.id === draggingZoneId.value);
                if (activeOrigZone) {
                    const activeOrigC = getShapeCentroid(activeOrigZone);
                    const activeOrigP = getPointByIndex(activeOrigZone, draggingPointIndex.value);
                    const distOrig = Math.hypot(activeOrigP.x - activeOrigC.x, activeOrigP.y - activeOrigC.y) || 1;
                    const distNew = Math.hypot(xNatural - activeOrigC.x, yNatural - activeOrigC.y);
                    const scale = distNew / distOrig;

                    selectedZoneIndices.value.forEach(id => {
                        const orig = originalDragState.value.find(z => z.id === id);
                        const current = zones.value.find(z => z.id === id);
                        if (!orig || !current) return;
                        
                        const origC = getShapeCentroid(orig);
                        if (current.shape === 'rectangle') {
                            current.width = orig.width * scale; current.height = orig.height * scale;
                            current.x = origC.x - (current.width / 2); current.y = origC.y - (current.height / 2);
                        } else if (current.shape === 'circle') { current.radius = orig.radius * scale; } 
                        else if (current.shape === 'polygon') {
                            current.points = orig.points.map(p => ({ x: origC.x + (p.x - origC.x) * scale, y: origC.y + (p.y - origC.y) * scale }));
                        }
                    });
                }
            }
            dragStart.value = { x: xCanvas, y: yCanvas };
            redrawAllZones();
            return; 
        }

        const isActivePolygon = currentMode.value === 'polygon' && points.value.length > 0;
        if ((!drawing.value && !isActivePolygon) || !currentShape.value) return;

        ctxVal.clearRect(0, 0, canvas.width, canvas.height);
        redrawAllZones(); 

        const shape = currentShape.value;
        if (!shape) return;

        if (shape.shape === 'rectangle') { shape.width = xNatural - shape.x; shape.height = yNatural - shape.y; drawZoneShape(shape) } 
        else if (shape.shape === 'circle') { const dx = xNatural - shape.x, dy = yNatural - shape.y; shape.radius = Math.sqrt(dx * dx + dy * dy); drawZoneShape(shape) } 
        else if (shape.shape === 'polygon') {
            drawZoneShape(shape)
            if (points.value.length > 0) {
                const lastPoint = points.value[points.value.length - 1];
                ctx.beginPath(); ctx.moveTo(lastPoint.x, lastPoint.y); ctx.lineTo(xNatural, yNatural);
                ctx.strokeStyle = shape.color; ctx.lineWidth = 2; ctx.setLineDash([6, 4]); ctx.stroke(); ctx.setLineDash([]); 
            }
        }
    }

    const handleMouseUp = async () => {
        const mode = currentMode.value;
        
        if (mode === 'move' && selectedZoneIndices.value.length > 0 && dragStart.value) {
            dragStart.value = null; // Lock immediately
            const updatePromises = selectedZoneIndices.value.map(id => {
                const zone = zones.value.find(z => z.id === id);
                if (zone) {
                    const payload = transformToFlatCoordinates(zone);
                    payload.project = mediaData.value.project;
                    return updateAOI(payload);
                }
            });
            await Promise.all(updatePromises);
            if (mediaData.value) fetchMediaAnalysis(mediaData.value.project, mediaData.value.id);
            return;
        }

        if (mode === 'edit-points' && draggingZoneId.value !== null && dragStart.value) {
            const isMulti = selectedZoneIndices.value.length > 1;
            const targetId = draggingZoneId.value;

            draggingPointIndex.value = null; // Lock immediately
            draggingZoneId.value = null;
            dragStart.value = null;
            originalDragState.value = null;

            if (isMulti) {
                const updatePromises = selectedZoneIndices.value.map(id => {
                    const zone = zones.value.find(z => z.id === id);
                    if (zone) {
                        const payload = transformToFlatCoordinates(zone);
                        payload.project = mediaData.value.project;
                        return updateAOI(payload);
                    }
                });
                await Promise.all(updatePromises);
            } else {
                const zone = zones.value.find(z => z.id === targetId);
                if (zone) {
                    const payload = transformToFlatCoordinates(zone);
                    payload.project = mediaData.value.project;
                    await updateAOI(payload);
                }
            }
            if (mediaData.value) fetchMediaAnalysis(mediaData.value.project, mediaData.value.id);
            return;
        }
        
        drawing.value = false;
        const shape = currentShape.value;

        if (shape && (shape.shape === 'rectangle' || shape.shape === 'circle')) {
            if (shape.shape === 'rectangle') {
                if (shape.width < 0) { shape.x += shape.width; shape.width = -shape.width; }
                if (shape.height < 0) { shape.y += shape.height; shape.height = -shape.height; }
            }
            const selItem = mediaData.value; 
            shape.name = ""; shape.project = selItem ? selItem.project : null; shape.media = selItem ? selItem.id : null;
            zones.value.push(shape);
            openEditLabelDialog(zones.value.length - 1);
            currentShape.value = null;
            ctx.clearRect(0, 0, drawingCanvas.value.width, drawingCanvas.value.height);
            redrawAllZones(); 
        }
    }

    const copySelectedAOIs = () => {
        if (selectedZoneIndices.value.length === 0) return;
        clipboardAOIs.value = selectedZoneIndices.value.map(id => {
            const zone = zones.value.find(z => z.id === id);
            return zone ? JSON.parse(JSON.stringify(zone)) : null;
        }).filter(Boolean);
        
        snackBarMessage.value = `${clipboardAOIs.value.length} AOI(s) copied to clipboard`;
        isSnackbarVisible.value = true;
    };

    const pasteAOIs = async () => {
        if (clipboardAOIs.value.length === 0) return;
        const newAOIsPayload = []; const offset = 20;
        
        clipboardAOIs.value.forEach(zone => {
            const newZone = JSON.parse(JSON.stringify(zone)); delete newZone.id; 
            newZone.name = (newZone.name || "AOI") + " copy";
            if (newZone.shape === 'rectangle' || newZone.shape === 'circle') { newZone.x += offset; newZone.y += offset; } 
            else if (newZone.shape === 'polygon') { newZone.points.forEach(p => { p.x += offset; p.y += offset; }); }
            
            const payload = transformToFlatCoordinates(newZone);
            payload.project = mediaData.value.project; payload.media = mediaData.value.id;
            newAOIsPayload.push(payload);
        });

        try {
            const payloadObj = { project: mediaData.value.project, media: mediaData.value.id, aois: newAOIsPayload, nets: [] };
            const response = await bulkCreateAOIs(payloadObj);
            
            if (response && response.aois) {
                zones.value.push(...response.aois);
                selectedZoneIndices.value = response.aois.map(z => z.id);
                selectedZoneIndex.value = selectedZoneIndices.value[selectedZoneIndices.value.length - 1];
                redrawAllZones();
                snackBarMessage.value = `${response.aois.length} AOI(s) pasted and saved`;
                isSnackbarVisible.value = true;
                fetchMediaAnalysis(mediaData.value.project, mediaData.value.id);
            }
        } catch (error) { console.error("Paste failed", error); }
    };

    const handleKeyDown = (e) => {
        if (['INPUT', 'TEXTAREA'].includes(e.target.tagName)) return;
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c') { copySelectedAOIs(); e.preventDefault(); }
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v') { pasteAOIs(); e.preventDefault(); }
    };

    const isPointInRectangle = (pointX, pointY, rect) => (pointX >= rect.x && pointX <= rect.x + rect.width && pointY >= rect.y && pointY <= rect.y + rect.height);
    const isPointInCircle = (pointX, pointY, circle) => (Math.sqrt((pointX - circle.x) ** 2 + (pointY - circle.y) ** 2) <= circle.radius);
    const isPointInPolygon = (pointX, pointY, polygonPoints) => {
        let intersect = 0;
        for (let i = 0, j = polygonPoints.length - 1; i < polygonPoints.length; j = i++) {
            const xi = polygonPoints[i].x, yi = polygonPoints[i].y, xj = polygonPoints[j].x, yj = polygonPoints[j].y;
            if (((yi > pointY) !== (yj > pointY)) && (pointX < (xj - xi) * (pointY - yi) / (yj - yi) + xi)) intersect++;
        }
        return intersect % 2 === 1;
    };
    const findZoneAtCoordinates = (xNatural, yNatural) => {
        for (let i = zones.value.length - 1; i >= 0; i--) {
            const zone = zones.value[i];
            if (zone.shape === 'rectangle' && isPointInRectangle(xNatural, yNatural, zone)) return zone.id;
            else if (zone.shape === 'circle' && isPointInCircle(xNatural, yNatural, zone)) return zone.id;
            else if (zone.shape === 'polygon' && isPointInPolygon(xNatural, yNatural, zone.points)) return zone.id;
        }
        return null; 
    };

    // 4. Sidebar / Actions
    const selectZone = (index, multi = false) => {
        if (multi) toggleItemInArray(selectedZoneIndices, index);
        else selectedZoneIndices.value = [index];
        selectedZoneIndex.value = selectedZoneIndices.value.length > 0 ? selectedZoneIndices.value[selectedZoneIndices.value.length - 1] : null;
        selectedGroupIndex.value = null; redrawAllZones();
    }
    const toggleItemInArray = (arrRef, item) => {
        const index = arrRef.value.indexOf(item);
        if (index === -1) arrRef.value.push(item); else arrRef.value.splice(index, 1);
    }

    const openEditLabelDialog = (index, group=false) => {
        if (group) { selectedGroupIndex.value = index; editedZoneLabel.value = groups.value[index].name || '';
        } else { selectedGroupIndex.value = null; editedZoneLabel.value = zones.value[index].name || ''; editedElement.value = zones.value[index].element || ''; }
        editingZoneIndex.value = index; showEditLabelDialog.value = true;
    };

    const saveEditedAOI = async () => {
        if ( editingZoneIndex.value !== null && editedZoneLabel.value.trim() !== '') {
            let zone = {}
            if (selectedGroupIndex.value === null) {
                zones.value[editingZoneIndex.value].name = editedZoneLabel.value.trim(); zone = zones.value[editingZoneIndex.value]; zone.element = editedElement.value
                const payload = transformToFlatCoordinates(zone)
                if (zone.id === undefined || zone.id === null) { zones.value[editingZoneIndex.value] = await addAOI(payload); fetchMediaAnalysis(payload.project, payload.media) }
                else await updateAOI(payload)
            } else {
                groups.value[editingZoneIndex.value].name = editedZoneLabel.value.trim(); zone = groups.value[editingZoneIndex.value];
                if (zone.id === undefined || zone.id === null) groups.value[editingZoneIndex.value] = await addNet(zone); else updateNet(zone)
            }
        }
        editingZoneIndex.value = null; showEditLabelDialog.value = false; editedZoneLabel.value = ''; editedElement.value = '';
    }
    
    const cancelEditAOI = () => {
        if (editingZoneIndex.value !== null && zones.value[editingZoneIndex.value]?.name.trim() === '') zones.value.splice(editingZoneIndex.value, 1)
        if (editingZoneIndex.value !== null && selectedGroupIndex.value !== null && groups.value[editingZoneIndex.value]?.name.trim() === '') groups.value.splice(editingZoneIndex.value, 1)
        redrawAllZones(); editingZoneIndex.value = null; showEditLabelDialog.value = false; editedZoneLabel.value = ''; editedElement.value = '';
    }

    const openDeleteConfirmDialog = (index) => { editingZoneIndex.value = index; showDeleteConfirmDialog.value = true; };
    const cancelDeleteAoi = () => { showDeleteConfirmDialog.value = false; };
    const openDeleteNetConfirmDialog = (index) => { editingZoneIndex.value = index; showDeleteNetConfirmDialog.value = true; };
    const cancelDeleteNet = () => { showDeleteNetConfirmDialog.value = false; };

    const deleteZone = async () => {
        try {
            const zone = zones.value[editingZoneIndex.value];
            const zoneId = zone.id; 
            
            await deleteAOI(zone);
            
            if (zoneId) {
                const affectedGroups = [];
                
                groups.value.forEach(group => {
                    if (group.aois && group.aois.includes(zoneId)) {
                        // Remove the deleted AOI ID from the group
                        group.aois = group.aois.filter(id => id !== zoneId);
                        affectedGroups.push(group);
                    }
                });

                // If any groups were modified, save them to the backend concurrently
                if (affectedGroups.length > 0) {
                    await Promise.all(affectedGroups.map(group => updateNet(group)));
                }
            }

            zones.value.splice(editingZoneIndex.value, 1);
            editingZoneIndex.value = null;
            selectedZoneIndex.value = null; 
            
            selectedZoneIndices.value = selectedZoneIndices.value.filter(id => id !== zoneId);
            
            fetchMediaAnalysis(mediaData.value.project, zone.media);
            redrawAllZones(); 
            showDeleteConfirmDialog.value = false;
            
        } catch (error) { 
            console.error("Failed to delete AOI", error); 
        }
    }
    
    const deleteGroup = async () => {
        try {
            const targetIndex = editingZoneIndex.value; const group = groups.value[targetIndex];
            await deleteNet(group); groups.value.splice(targetIndex, 1); 
            editingZoneIndex.value = null; selectedGroupIndex.value = null; 
            selectedZoneIndices.value = selectedZoneIndices.value.filter(i => i !== targetIndex); 
            fetchMediaAnalysis(mediaData.value.project, group.media);
            redrawAllZones(); showDeleteNetConfirmDialog.value = false;
        } catch (error) { console.error("Failed to delete Net", error); }
    }

    const transformToFlatCoordinates = (zone) => {
        const reverseScale = 1 / dataScale.value; // Scale back up to original DB size
        const { x, y, width, height, radius, points, ...metaData } = zone;
        let coordinates = []; 
        
        const toInt = (val) => Math.round(Number(val) * reverseScale);

        if (zone.shape === 'polygon' && Array.isArray(points)) {
            coordinates = points.flatMap(p => [toInt(p.x), toInt(p.y)]);
        } else if (zone.shape === 'rectangle') {
            coordinates = [x, y, x + width, y + height].map(toInt);
        } else if (zone.shape === 'circle') {
            coordinates = [toInt(x), toInt(y), toInt(radius)]; 
        }

        return { ...metaData, coordinates };
    };

    const drawKpiOverlay = (zone) => {
        if (!ctx || !analysisData.value[zone.id]) return;
        const dataObj = analysisData.value[zone.id]["time_slots"]["Full"];
        if (!dataObj) return;

        const metrics = Array.isArray(selectedKpiMetric.value) ? selectedKpiMetric.value : [];
        if (metrics.length === 0) return;
        const lines = [];

        metrics.forEach(metric => {
            const value = dataObj[metric];
            if (value === undefined || value === null) return;
            let formattedValue = '';
            let label = metric.toUpperCase() === 'TTFF' ? 'TTFF' : metric.charAt(0).toUpperCase() + metric.slice(1);
            label = label === 'Duration_percent' ? '% Duration' : label
            if (metric === 'duration' || metric === 'ttff') formattedValue = `${Math.round(value)}ms`;
            else if (metric === 'order') formattedValue = `${value}`;
            else formattedValue = `${Number(value).toFixed(1)}%`;
            lines.push(`${label}: ${formattedValue}`);
        });

        if (lines.length === 0) return;
        const naturalCentroid = getShapeCentroid(zone);
        const renderedPos = convertToRendered(naturalCentroid.x, naturalCentroid.y);
        const cx = renderedPos.x; const cy = renderedPos.y;

        ctx.font = "12px Arial"; const padding = 6; const lineHeight = 18;
        let maxTextWidth = 0;
        lines.forEach(line => { const width = ctx.measureText(line).width; if (width > maxTextWidth) maxTextWidth = width; });

        const boxWidth = maxTextWidth + (padding * 2); const boxHeight = (lines.length * lineHeight) + (padding * 2);

        ctx.fillStyle = "rgba(0, 0, 0, 0.7)"; ctx.fillRect(cx - (boxWidth / 2), cy - (boxHeight / 2), boxWidth, boxHeight);
        ctx.fillStyle = "white"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
        let textY = (cy - (boxHeight / 2)) + padding + (lineHeight / 2);

        lines.forEach(line => { ctx.fillText(line, cx, textY); textY += lineHeight; });
    };

    const getShapeCentroid = (zone) => {
        if (zone.shape === 'rectangle') return { x: zone.x + zone.width / 2, y: zone.y + zone.height / 2 };
        else if (zone.shape === 'circle') return { x: zone.x, y: zone.y };
        else if (zone.shape === 'polygon') {
            const sum = zone.points.reduce((acc, p) => ({ x: acc.x + p.x, y: acc.y + p.y }), { x: 0, y: 0 });
            return { x: sum.x / zone.points.length, y: sum.y / zone.points.length };
        }
        return { x: 0, y: 0 };
    };

    const getPointByIndex = (zone, index) => {
        if (zone.shape === 'polygon') return { x: zone.points[index].x, y: zone.points[index].y };
        if (zone.shape === 'rectangle') {
            switch(index) {
                case 0: return { x: zone.x, y: zone.y }; case 1: return { x: zone.x + zone.width, y: zone.y };
                case 2: return { x: zone.x, y: zone.y + zone.height }; case 3: return { x: zone.x + zone.width, y: zone.y + zone.height };
            }
        }
        if (zone.shape === 'circle') {
            switch(index) {
                case 0: return { x: zone.x + zone.radius, y: zone.y }; case 1: return { x: zone.x - zone.radius, y: zone.y };
                case 2: return { x: zone.x, y: zone.y + zone.radius }; case 3: return { x: zone.x, y: zone.y - zone.radius };
            }
        }
        return { x: 0, y: 0 };
    };

    const addZone = () => {
        if (currentShape.value && currentShape.value.shape === 'polygon' && points.value.length > 2) {
            currentShape.value.name = ""; currentShape.value.project = mediaData.value.project; currentShape.value.media = mediaData.value.id;
            currentShape.value.points = [...points.value]; zones.value.push(currentShape.value);
            openEditLabelDialog(zones.value.length-1); points.value = []; currentShape.value = null; redrawAllZones();
        }
    }

    const undoDraw = () => { if ( currentShape.value && currentShape.value.shape === 'polygon') { points.value.pop(); redrawAllZones(); } }

    const groupZones = () => {
        if (selectedZoneIndices.value.length > 1) {
            const newGroupId = generateGroupId();
            const newGroup = { temp_id: newGroupId, name: "", project: mediaData.value.project, media: mediaData.value.id, aois: [...selectedZoneIndices.value] };
            groups.value.push(newGroup); selectedZoneIndices.value = []; selectedZoneIndex.value = null; redrawAllZones(); openEditLabelDialog(groups.value.length-1,true)
        }
    }

    const saveZones = () => { mediaData.value.aois = zones.value; snackBarMessage.value = "Zones saved!"; isSnackbarVisible.value = true; }

    const selectGroup = (index) => {
        selectedZoneIndices.value = []
        if (selectedGroupIndex.value != index){ selectedGroupIndex.value = index; groups.value[index].aois.forEach(aoi => selectZone(aoi, true)) } 
        else { selectedGroupIndex.value = null; redrawAllZones(); }
    }

    const exportCanvas = (type) => {
        if (!mediaData.value.media) return;
        const img = new Image(); img.crossOrigin = "anonymous"; img.src = mediaData.value.media + (mediaData.value.media.indexOf('?') > -1 ? '&t=' : '?t=') + new Date().getTime();
        img.onload = () => {
            const tempCanvas = document.createElement('canvas'); tempCanvas.width = workingWidth.value; tempCanvas.height = workingHeight.value;
            const exportCtx = tempCanvas.getContext('2d', { willReadFrequently: true }); 
            if (isGrayscale.value) exportCtx.filter = 'grayscale(100%)';
            try { exportCtx.drawImage(img, 0, 0, workingWidth.value, workingHeight.value); } catch(e) { return; }
            exportCtx.filter = 'none';
            if (type === 'heatmap' && heatmapContainer.value) {
                const hmCanvas = heatmapContainer.value.querySelector('canvas');
                if (hmCanvas) exportCtx.drawImage(hmCanvas, 0, 0, workingWidth.value, workingHeight.value);
            } else if (type === 'aoi' && drawingCanvas.value) { exportCtx.drawImage(drawingCanvas.value, 0, 0, workingWidth.value, workingHeight.value);
            } else if (type === 'scanpath' && scanpathContainer.value) {
                const svgString = new XMLSerializer().serializeToString(scanpathContainer.value); const svgImg = new Image();
                svgImg.onload = () => { exportCtx.drawImage(svgImg, 0, 0, workingWidth.value, workingHeight.value); triggerDownload(tempCanvas.toDataURL("image/png"), type); }
                svgImg.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svgString); return;
            }
            triggerDownload(tempCanvas.toDataURL("image/png"), type);
        };
    }
    const triggerDownload = (dataUrl, type) => {
        const link = document.createElement('a'); link.download = `${mediaData.value.name || 'export'}_${type}_${isGrayscale.value ? 'bw' : 'color'}.png`;
        link.href = dataUrl; document.body.appendChild(link); link.click(); document.body.removeChild(link);
    }

    const exportVideo = async () => {
        if (!mediaData.value.media || isMetMedia.value) return;
        isRecording.value = true;
        recordingProgress.value = 0;

        const originalTimeRange = [...timeRange.value];
        const canvas = document.createElement('canvas');
        canvas.width = workingWidth.value;
        canvas.height = workingHeight.value;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });

        const img = new Image();
        img.crossOrigin = "anonymous";
        img.src = mediaData.value.media + (mediaData.value.media.indexOf('?') > -1 ? '&t=' : '?t=') + new Date().getTime();
        await new Promise(resolve => img.onload = resolve);

        // Force Heatmap to be visible
        const originalHeatmapShow = showHeatmap.value;
        showHeatmap.value = true;

        const tMin = timeRange.value[0];
        const tMax = timeRange.value[1];

        const fps = 30;
        const durationSeconds = (tMax - tMin) / 1000; 
        
        if (durationSeconds <= 0) {
            isRecording.value = false;
            return;
        }

        const totalFrames = Math.round(durationSeconds * fps);
        const timeIncrement = (tMax - tMin) / totalFrames;

        const drawProgressionKpis = (exportCtx, simTimeMs) => {
            const metrics = Array.isArray(selectedKpiMetric.value) ? selectedKpiMetric.value : [];
            if (metrics.length === 0 || zones.value.length === 0) return;

            const simTimeSec = Math.floor(simTimeMs / 1000);

            zones.value.forEach(zone => {
                if (!analysisData.value[zone.id] || !analysisData.value[zone.id]["time_slots"]) return;
                
                const timeSlots = analysisData.value[zone.id]["time_slots"];
                let slotKey = "Full"; 
                
                const availableSecs = Object.keys(timeSlots).filter(k => k !== "Full" && !isNaN(Number(k))).map(Number).sort((a,b) => a-b);
                const validSecs = availableSecs.filter(s => s <= Math.max(1, simTimeSec));
                
                if (validSecs.length > 0) {
                    slotKey = validSecs[validSecs.length - 1].toString();
                } else if (simTimeSec <= 0) {
                    slotKey = null; 
                }

                let dataObj = slotKey ? timeSlots[slotKey] : null;
                
                if (!dataObj || simTimeSec === 0) {
                    dataObj = {};
                    metrics.forEach(m => dataObj[m] = 0);
                }

                const lines = [];
                metrics.forEach(metric => {
                    const value = dataObj[metric] !== undefined ? dataObj[metric] : 0;
                    if (value === 0 || value === null) return
                    let formattedValue = ''; 
                    let label = metric.toUpperCase() === 'TTFF' ? 'TTFF' : metric.charAt(0).toUpperCase() + metric.slice(1);
                    label = label === 'Duration_percent' ? '% Duration' : label
                    if (metric === 'duration' || metric === 'ttff') {
                        formattedValue = `${Math.round(value)}ms`;
                    } else if (metric === 'order') {
                        formattedValue = `${value}`;
                    } else {
                        formattedValue = `${Number(value).toFixed(1)}%`;
                    }
                    lines.push(`${label}: ${formattedValue}`);
                });

                if (lines.length === 0) return;

                const naturalCentroid = getShapeCentroid(zone);
                const cx = naturalCentroid.x; 
                const cy = naturalCentroid.y;

                exportCtx.font = "14px Arial"; 
                const padding = 8; 
                const lineHeight = 20;
                let maxTextWidth = 0;
                
                lines.forEach(line => { 
                    const width = exportCtx.measureText(line).width; 
                    if (width > maxTextWidth) maxTextWidth = width; 
                });

                const boxWidth = maxTextWidth + (padding * 2); 
                const boxHeight = (lines.length * lineHeight) + (padding * 2);

                exportCtx.fillStyle = "rgba(0, 0, 0, 0.75)"; 
                exportCtx.fillRect(cx - (boxWidth / 2), cy - (boxHeight / 2), boxWidth, boxHeight);
                
                exportCtx.fillStyle = "white"; 
                exportCtx.textAlign = "center"; 
                exportCtx.textBaseline = "middle";
                
                let textY = (cy - (boxHeight / 2)) + padding + (lineHeight / 2);

                lines.forEach(line => { 
                    exportCtx.fillText(line, cx, textY); 
                    textY += lineHeight; 
                });
            });
        };
        
        const preRenderedFrames = [];
        let simulatedTime = tMin;

        for (let i = 0; i <= totalFrames; i++) {
            timeRange.value = [tMin, simulatedTime];
            updateHeatmapVisualization();

            await new Promise(resolve => setTimeout(resolve, 15));

            ctx.clearRect(0, 0, canvas.width, canvas.height);
            if (isGrayscale.value) ctx.filter = 'grayscale(100%)';
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            ctx.filter = 'none';
            
            if (heatmapContainer.value) {
                const hmCanvas = heatmapContainer.value.querySelector('canvas');
                if (hmCanvas) ctx.drawImage(hmCanvas, 0, 0, canvas.width, canvas.height);
            }

            // drawProgressionKpis(ctx, simulatedTime);

            const bitmap = await createImageBitmap(canvas);
            preRenderedFrames.push(bitmap);

            simulatedTime += timeIncrement;
            recordingProgress.value = Math.round((i / totalFrames) * 50); 
        }

        const stream = canvas.captureStream(fps); 
        let options = { mimeType: 'video/mp4' };
        if (MediaRecorder.isTypeSupported('video/mp4')) {
            options = { mimeType: 'video/mp4; codecs="avc1.42E01E, mp4a.40.2"' };
        } else if (MediaRecorder.isTypeSupported('video/webm; codecs=vp9')) {
            options = { mimeType: 'video/webm; codecs=vp9' };
        }

        let recorder;
        try {
            recorder = new MediaRecorder(stream, options);
        } catch(e) {
            snackBarMessage.value = "Video export not supported in this browser.";
            isSnackbarVisible.value = true;
            isRecording.value = false;
            return;
        }

        const chunks = [];
        recorder.ondataavailable = e => { if (e.data.size > 0) chunks.push(e.data); };
        
        recorder.onstop = () => {
            const blob = new Blob(chunks, { type: options.mimeType });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${mediaData.value.name || 'heatmap'}_progression.mp4`;
            document.body.appendChild(a); a.click(); document.body.removeChild(a);
            URL.revokeObjectURL(url);

            preRenderedFrames.forEach(bmp => bmp.close());

            timeRange.value = originalTimeRange;
            showHeatmap.value = originalHeatmapShow;
            updateHeatmapVisualization();
            isRecording.value = false;
        };

        recorder.start();

        let startTime = null;
        const exactDurationMs = durationSeconds * 1000;

        const playNextFrame = (currentTime) => {
            if (!startTime) startTime = currentTime;
            
            const elapsed = currentTime - startTime;
            
            let frameIdx = Math.floor((elapsed / exactDurationMs) * totalFrames);

            if (frameIdx < 0) frameIdx = 0;
            if (frameIdx >= preRenderedFrames.length) frameIdx = preRenderedFrames.length - 1;

            if (elapsed >= exactDurationMs) {
                recorder.stop();
                return;
            }

            if (preRenderedFrames[frameIdx]) {
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                ctx.drawImage(preRenderedFrames[frameIdx], 0, 0);
            }
            
            recordingProgress.value = 50 + Math.round((frameIdx / preRenderedFrames.length) * 50);

            requestAnimationFrame(playNextFrame);
        };

        requestAnimationFrame(playNextFrame);
    };

    /*** SCANPATH ***/
    const currentIndex = ref(0);
    const DURATION_SCALE = 0.3; 
    const MIN_RADIUS = 15;

    // Only count them, don't map them
    const totalRespondents = computed(() => scanpathData.value?.respondents?.length || 0);

    // Get the raw active respondent
    const currentRespondent = computed(() => {
        if (!scanpathData.value?.respondents) return null;
        return scanpathData.value.respondents[currentIndex.value];
    });

    const currentRespondentName = computed(() => currentRespondent.value?.respondent || 'Unknown');

    // Calculate ONLY for the current respondent
    const currentFixations = computed(() => {
        if (!currentRespondent.value) return [];
        
        const tMin = timeRange.value[0];
        const tMax = timeRange.value[1];

        return (currentRespondent.value.fixations || []).filter(fix => {
            let s = fix.start ?? fix.start_time ?? fix.fix_start ?? fix.timestamp;
            if (s === undefined) return true;
            let e = fix.end ?? fix.end_time ?? fix.fix_end ?? (s + (fix.duration || 0));
            return s <= tMax && e >= tMin;
        }).map((f, i) => ({ ...f, index: i })); // Create reactive objects only for the viewable ones
    });

    const saccadePoints = computed(() => { 
        return currentFixations.value.map(f => `${f.fix_x},${f.fix_y}`).join(' '); 
    });
    
    const scanpaths = computed(() => {
        const tMin = timeRange.value[0];
        const tMax = timeRange.value[1];

        return (scanpathData.value?.respondents || []).map((resp, index) => {
            const filteredFixations = (resp.fixations || []).filter(fix => {
                let s = fix.start ?? fix.start_time ?? fix.fix_start ?? fix.timestamp;
                if (s === undefined) return true;
                let e = fix.end ?? fix.end_time ?? fix.fix_end ?? (s + (fix.duration || 0));
                return s <= tMax && e >= tMin;
            }).map((f, i) => ({ ...f, index: i }));

            return { ...resp, index, fixations: filteredFixations }
        });
    });
    const startArrowPath = computed(() => {
        if (!currentFixations.value || currentFixations.value.length === 0) return null;
        const firstFix = currentFixations.value[0]; const secondFix = currentFixations.value[1];
        const cx = firstFix.fix_x; const cy = firstFix.fix_y;
        const dx = secondFix !== undefined ? secondFix.fix_x - cx : -cx; const dy = secondFix !== undefined ? secondFix.fix_y - cy : -cy;
        const angle = Math.atan2(dy, dx);
        const arrowLength = 50; const arrowWidth = 25; 
        const transform = (x, y) => { const rx = x * Math.cos(angle) - y * Math.sin(angle); const ry = x * Math.sin(angle) + y * Math.cos(angle); return `${cx + rx} ${cy + ry}`; };
        const startX = -arrowLength;
        return `M ${transform(startX, -arrowWidth)} L ${transform(startX + arrowLength, 0)} L ${transform(startX, arrowWidth)} L ${transform(startX + arrowLength * 0.3, 0)} Z`;
    });
    const calculateRadius = (duration) => Math.min(Math.max(duration * DURATION_SCALE, MIN_RADIUS), 60);
    const nextRespondent = () => { if (currentIndex.value < totalRespondents.value - 1) currentIndex.value++; };
    const prevRespondent = () => { if (currentIndex.value > 0) currentIndex.value--; };

    const aoisImport = async (importedAOIs) => {
        let aois = []
        let nets = importedAOIs?.nets || []
        importedAOIs.aois.map(aoi => aois.push(transformToFlatCoordinates(aoi)))
        const aoiPayload = { media: importedAOIs.media, project: importedAOIs.project, aois: aois, nets: nets }
        const newAOIs = await bulkCreateAOIs(aoiPayload)
        
        if (aois.length > 0) zones.value.push(...newAOIs.aois);
        if (nets.length > 0) groups.value.push(...newAOIs.nets);
        selectedZoneIndex.value = null; selectedZoneIndices.value = [];
        redrawAllZones();
        fetchMediaAnalysis(mediaData.value.project, mediaData.value.id);
    }

    const groupedAOIElements = computed(() => {
        const sorted = [...elementsOptions.value].sort((a, b) => a.type.localeCompare(b.type));
        const finalArray = []; let lastType = null;
        sorted.forEach((item) => {
            if (item.type !== lastType) {
                if (lastType !== null) finalArray.push({ divider: true });
                finalArray.push({ header: item.type }); lastType = item.type;
            }
            finalArray.push(item);
        });
        return finalArray;
    })
    
    watch(showScanpath, async (newVal) => {
        if (newVal && !hasLoadedScanpath.value) {
            await fetchScanpath(mediaData.value.project, mediaData.value.id);
            
            // Scale scanpath data down
            if (scanpathData.value?.respondents) {
                scanpathData.value.respondents.forEach(r => {
                    (r.fixations || []).forEach(f => {
                        f.fix_x *= dataScale.value;
                        f.fix_y *= dataScale.value;
                    });
                });
            }
            
            // Freeze to prevent Vue reactivity from crashing the browser
            if (scanpathData.value) Object.freeze(scanpathData.value);
            
            hasLoadedScanpath.value = true;
            extractTimeBounds();
        }
    });

    watch(showHeatmap, async (newVal) => {
        if (newVal && !hasLoadedHeatmap.value) {
            await fetchMediaHeatmap(mediaData.value.project, mediaData.value.id);
            
            // Scale heatmap data down
            if (heatmapData.value?.data) {
                heatmapData.value.data.forEach(p => {
                    p.x *= dataScale.value;
                    p.y *= dataScale.value;
                });
            }
            
            // Freeze to prevent Vue reactivity from crashing the browser
            if (heatmapData.value) Object.freeze(heatmapData.value);
            
            hasLoadedHeatmap.value = true;
            extractTimeBounds();
            updateHeatmapVisualization();
        }
    });

    watch(() => props.selectedItem, () => onImageLoad());
    watch([showKPIs, selectedKpiMetric], () => redrawAllZones());
    watch(showEditLabelDialog, (isOpen) => { if (!isOpen && editingZoneIndex.value !== null) cancelEditAOI(); });

    onMounted(() => {
        fetchAOIElements();
        document.addEventListener('keydown', handleKeyDown);
    })
    
    onBeforeUnmount(() => {
        document.removeEventListener('keydown', handleKeyDown);
    })

    return {
        drawingCanvas, eftImage, heatmapContainer, aoiDrawer, scanpathContainer,
        zoomContainerStyle, points, scrollContainer, workingWidth, workingHeight,
        
        zoomLevel, showHeatmap, showAOIs, isGrayscale, showKPIs, showScanpath, showImport,
        modeOptions, currentMode, heatmapOpacity, heatmapRadius,
        selectedKpiMetric, kpiMetricsOptions, clipboardAOIs, showHelpDialog,
        
        timeRangeSeconds, minTimeSeconds, maxTimeSeconds, updateHeatmapVisualization,
        isRecording, recordingProgress, isMetMedia,

        zones, selectedZoneIndices, groups, selectedGroupIndex, hasLoadedScanpath,
        showEditLabelDialog, editedZoneLabel, editedElement, isSnackbarVisible, snackBarMessage, showKPITable, showDeleteConfirmDialog, showDeleteNetConfirmDialog, 
        
        heatmapLoading, analysisLoading, analysisData, summaryAnalysisData, aoiLoading, netLoading, scanpathData, scanpathLoading, mediasLoading, elementsLoading, groupedAOIElements,
        onImageLoad, handleMouseDown, handleMouseMove, handleMouseUp,
        selectZone, openEditLabelDialog, saveEditedAOI, cancelEditAOI, deleteZone, deleteGroup, copySelectedAOIs, pasteAOIs,
        opacityChange: (v) => heatmapInstance?.configure({minOpacity: v - 0.78, maxOpacity: v}),
        
        addZone, undoDraw, groupZones, selectGroup, saveZones, exportCanvas, exportVideo, aoisImport,
        openDeleteConfirmDialog, cancelDeleteAoi, openDeleteNetConfirmDialog, cancelDeleteNet,

        icons: {
            mdiVectorRectangle, mdiVectorPolygon, mdiVectorCircle, mdiCursorMove, mdiPencil,
            mdiPlus, mdiContentSaveOutline, mdiUndo, mdiGroup, mdiDelete, mdiFileDownloadOutline,
            mdiFire, mdiDrawingBox, mdiInvertColors, mdiChartBar, mdiTableLarge, mdiDownload,
            mdiClose, mdiFormatListBulleted, mdiMapMarkerPath, mdiContentCopy, mdiContentPaste,
            mdiHelpCircleOutline
        },
        
        currentIndex, DURATION_SCALE, MIN_RADIUS, scanpaths, totalRespondents,
        currentRespondentName, currentFixations, saccadePoints, startArrowPath,
        calculateRadius, nextRespondent, prevRespondent,
    }
  }
}
</script>

<style scoped>
.square-btn { min-width: 48px !important; width: 48px; height: 38px; padding: 0; }
.image-workspace-area { overflow-y: auto; padding: 0; display: flex; flex-direction: column; flex-grow: 1; }
.image-workspace-area > .v-row { flex-grow: 1; flex-wrap: nowrap; }
.image-canvas-viewport-col { flex-grow: 1; min-width: 0; padding: 0; display: flex; flex-direction: column; }
.image-canvas-scroll-container { flex-grow: 1; overflow-x: auto; overflow-y: auto; position: relative; padding: 12px; display: flex; align-items: center; }
.image-container { 
    position: relative; 
    display: block; 
    background-color: #f5f5f5; 
}

/* Force the image to match the container exactly, ignoring screen size */
.layer-base, .image-container > .v-img { 
    max-width: none !important; 
    position: absolute; 
    top: 0; 
    left: 0; 
    z-index: 1; 
}
#heatmap-container { z-index: 2; pointer-events: none; position: relative; overflow:hidden; }
.modes { margin-top: 10px; display: flex; gap: 10px; align-items: center; }
.controls { margin-top: 10px; display: flex; gap: 10px; align-items: center; }
#drawing-canvas.move { cursor: move; }
#drawing-canvas.edit-points { cursor: crosshair; }
#drawing-canvas { position: absolute; top: 0; left: 0; background-color: transparent; cursor: crosshair; z-index: 3; }
.layer-base { position: absolute; top: 0; left: 0; z-index: 1; }
.layer-overlay { position: absolute; top: 0; left: 0; width: 100%; height: 100%; z-index: 2; }
.grayscale-effect { filter: grayscale(100%); transition: filter 0.3s ease; }
.scanpath-svg { position: absolute; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; z-index: 20; }
.fixation-circle { transition: r 0.3s ease; }
.fixation-text { font-size: 12px; font-weight: 700; fill: white; pointer-events: none; font-family: 'Roboto', sans-serif; text-shadow: 0px 0px 3px rgba(0,0,0,0.8); }
.v-list-item.selected { background-color: #e3f2fd; }
</style>